import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface TouchSwipe {
	moveVector: {x: number, y: number};
	timestamp: number,
}

@Injectable({
	providedIn: 'root'
})
export class InputService {

	private _swipeDetected$: BehaviorSubject<TouchSwipe> = new BehaviorSubject<TouchSwipe>(null);
	private _vitaReactPush$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
	private _suspendDetectionVitaReact$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private _suspendDetectionVitaReact: boolean;
	private _suspendDetectionSwipe$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private _suspendDetectionSwipe: boolean;
	private _currentTouch$: BehaviorSubject<TouchSwipe> = new BehaviorSubject<TouchSwipe>(null);
	public readonly swipeDistance = window.innerWidth/5;
	private readonly maxFingerDistance = 150;

	private currentTouch: TouchSwipe;
	private touchStartTime: number;
	private touchStartLocation: {x: number, y: number};

	constructor() { }

	public set suspendDetectionVitaReact(suspendDetection: boolean) {
		this._suspendDetectionVitaReact = suspendDetection;
		this._suspendDetectionVitaReact$.next(suspendDetection);
	}

	public get suspendDetectionVitaReact(): boolean {
		return this._suspendDetectionVitaReact;
	}

	public get suspendDetectionVitaReact$(): Observable<boolean> {
		return this._suspendDetectionVitaReact$;
	}

	public set suspendDetectionSwipe(suspendDetection: boolean) {
		this._suspendDetectionSwipe = suspendDetection;
		this._suspendDetectionSwipe$.next(suspendDetection);
	}

	public get suspendDetectionSwipe(): boolean {
		return this._suspendDetectionSwipe;
	}

	public get suspendDetectionSwipe$(): Observable<boolean> {
		return this._suspendDetectionSwipe$;
	}

	public get swipeDetected$(): Observable<TouchSwipe> {
		return this._swipeDetected$;
	}

	public get vitaReactPush$(): Observable<number> {
		return this._vitaReactPush$;
	}

	public get currentTouch$(): Observable<TouchSwipe> {
		return this._currentTouch$;
	}

	public newVitaReactPush(): void {
		if (!this.suspendDetectionVitaReact) {
			this._vitaReactPush$.next(Date.now());
		}
	}

	public newSwipe(): void {
		if (!this.suspendDetectionSwipe) {
			this._swipeDetected$.next(this.currentTouch);
		}
	}

	public touchStart(touchEvent: TouchEvent): void {
		if (this.isValidTouchesDistance(touchEvent?.touches)) {
			const touch = touchEvent.touches[0];
			this.touchStartTime = Date.now();
			this.touchStartLocation = { x: touch.clientX, y: touch.clientY };
		}
	}

	public touchMove(touchEvent: TouchEvent): void {
		if (!this.isValidTouchesDistance(touchEvent?.touches)) {
			this.currentTouch = null;
			this.touchStartTime = null;
			this.touchStartLocation = null;
		} else {
			const touch = touchEvent.touches[0];
			const touchLocation = { x: touch.clientX, y: touch.clientY };
			const moveVector = { x: touchLocation.x - this.touchStartLocation.x, y: touchLocation.y - this.touchStartLocation.y };
			this.currentTouch = { moveVector: moveVector, timestamp: Date.now()};
			if (!this.suspendDetectionSwipe) {
				this._currentTouch$.next(this.currentTouch);
			}
		}
	}

	private isValidTouchesDistance(touches: TouchList): boolean {
		const distancesBetweenTouches = [];
		if (touches.length < 2) {
			return true;
		}
		for (let i = 0; i < touches.length-1; i++) {
			let shortestNextFingerDistance = Number.MAX_SAFE_INTEGER;
			for (let j = i + 1; j < touches.length; j++) {
				const vector = {
					x: touches[j].clientX - touches[i].clientX,
					y: touches[j].clientY - touches[i].clientY,
				};
				const distance = Math.sqrt(vector.x * vector.x + vector.y * vector.y );
				shortestNextFingerDistance = distance < shortestNextFingerDistance ? distance : shortestNextFingerDistance;
			}
			distancesBetweenTouches.push(shortestNextFingerDistance);
		}
		return distancesBetweenTouches.every(distance => distance < this.maxFingerDistance);
	}

	public touchEnd(): void {
		if (Math.abs(this.currentTouch?.moveVector.x) >= this.swipeDistance) {
			this.newSwipe();
		}

		this.currentTouch = null;
		this.touchStartTime = null;
		this.touchStartLocation = null;
		this._currentTouch$.next(this.currentTouch);
	}
}
